import { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "../../examples/Tables/DataTable";
import AdminSearch from "./components/AdminSearch";
import { buildUrl } from "api/base";
import { fetchWithCsrfRetry } from "api/csrf";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";

function fmtDate(val) {
  if (!val) return "";
  try {
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString();
  } catch {
    return String(val);
  }
}

export default function Session() {
  const { showSuccess, showError } = useSnackbar();
  const { showConfirm } = useConfirmDialog();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [busySid, setBusySid] = useState(null);
  const [killingAll, setKillingAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(buildUrl("/admin/sessions"), { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.sessions || []);
    } catch (e) {
      setError(e.message || "Ralat memuat sesi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return rows;
    const s = (v) => {
      if (v === null || v === undefined) return "";
      try { return String(v); } catch { return ""; }
    };
    return rows.filter((r) => [r.sid, r.id_pengguna, r.bahagian, r.issuedAt, r.expire]
      .some((v) => s(v).toLowerCase().includes(q))
    );
  }, [rows, query]);

  const killSid = useCallback(async (sid) => {
    if (!sid) return;
    const confirmed = await showConfirm({
      title: 'Batalkan Sesi',
      message: `Adakah anda pasti untuk membatalkan sesi ini?\n\nSID: ${sid}`,
      confirmText: 'Batalkan',
      type: 'error'
    });
    if (!confirmed) return;
    setBusySid(sid);
    try {
      const res = await fetchWithCsrfRetry(buildUrl("/admin/sessions/kill"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sid }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
      showSuccess('Sesi berjaya dibatalkan');
    } catch (e) {
      showError(`Gagal batal sesi: ${e.message}`);
    } finally {
      setBusySid(null);
    }
  }, [load]);

  const killUser = useCallback(async (id_pengguna) => {
    if (!id_pengguna) return;
    const confirmed = await showConfirm({
      title: 'Batalkan Semua Sesi Pengguna',
      message: `Adakah anda pasti untuk membatalkan SEMUA sesi untuk pengguna: ${id_pengguna}?`,
      confirmText: 'Batalkan Semua',
      type: 'error'
    });
    if (!confirmed) return;
    setBusySid(`user:${id_pengguna}`);
    try {
      const res = await fetchWithCsrfRetry(buildUrl("/admin/sessions/kill-user"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_pengguna }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
      showSuccess(`Semua sesi untuk ${id_pengguna} berjaya dibatalkan`);
    } catch (e) {
      showError(`Gagal batal sesi pengguna: ${e.message}`);
    } finally {
      setBusySid(null);
    }
  }, [load]);

  const killAll = useCallback(async (keepSelf = true) => {
    const title = keepSelf ? 'Batalkan Semua Sesi (Kecuali Anda)' : 'Batalkan SEMUA Sesi';
    const msg = keepSelf ?
      "Adakah anda pasti untuk membatalkan semua sesi KECUALI sesi anda?" :
      "Adakah anda pasti untuk membatalkan SEMUA sesi termasuk sesi anda? Anda akan dilog keluar!";
    const confirmed = await showConfirm({ title, message: msg, confirmText: 'Batalkan Semua', type: 'error' });
    if (!confirmed) return;
    setKillingAll(true);
    try {
      const res = await fetchWithCsrfRetry(buildUrl("/admin/sessions/kill-all"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keepSelf }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
      showSuccess('Semua sesi berjaya dibatalkan');
    } catch (e) {
      showError(`Gagal batal semua sesi: ${e.message}`);
    } finally {
      setKillingAll(false);
    }
  }, [load]);

  const columns = useMemo(() => [
    {
      Header: "ACTIONS",
      id: "__actions",
      accessor: "__actions",
      disableSortBy: true,
      // react-table passes a non-React-prop object; suppress prop-types lint for cell renderer
      // eslint-disable-next-line react/prop-types
      Cell: ({ row }) => {
        // eslint-disable-next-line react/prop-types
        const r = row?.original || {};
        const sid = r.sid;
        const idp = r.id_pengguna;
        const disabled = killingAll || busySid === sid || busySid === `user:${idp}`;
        return (
          <MDBox display="flex" gap={1}>
            <MDButton size="small" color="error" variant="outlined" disabled={disabled} onClick={() => killSid(sid)}>
              Kill
            </MDButton>
            {idp ? (
              <MDButton size="small" color="warning" variant="outlined" disabled={disabled} onClick={() => killUser(idp)}>
                Kill User
              </MDButton>
            ) : null}
          </MDBox>
        );
      },
    },
    { Header: "SID", accessor: "sid", width: "40%", align: "left" },
    { Header: "ID PENGGUNA", accessor: "id_pengguna", width: "15%", align: "left" },
    { Header: "BAHAGIAN", accessor: "bahagian", width: "15%", align: "left" },
    { Header: "Issued", accessor: "issuedAt", Cell: ({ value }) => fmtDate(value), width: "15%", align: "left" },
    { Header: "Expire", accessor: "expire", Cell: ({ value }) => fmtDate(value), width: "15%", align: "left" },
  ], [busySid, killingAll, killSid, killUser]);

  const tableRows = filtered.map((r) => ({
    ...r,
  }));

  if (loading) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}><CircularProgress size={24} /></MDBox>
    </DashboardLayout>
  );
  if (error) return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}><MDTypography color="error">{error}</MDTypography></MDBox>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={2}>
        <Card>
          <MDBox p={2} display="flex" flexDirection="column" gap={1}>
            <div>
              <MDTypography variant="h6">Sesi Pengguna</MDTypography>
              <MDTypography variant="button" color="text">Urus sesi: lihat, tamatkan satu, tamatkan semua</MDTypography>
            </div>
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton color="info" variant="outlined" size="small" onClick={load} disabled={killingAll || busySid !== null}>
                Refresh
              </MDButton>
              <MDButton color="warning" variant="outlined" size="small" onClick={() => killAll(true)} disabled={killingAll || busySid !== null}>
                Kill All (keep me)
              </MDButton>
              <MDButton color="error" variant="outlined" size="small" onClick={() => killAll(false)} disabled={killingAll || busySid !== null}>
                Kill All (including me)
              </MDButton>
            </MDBox>
          </MDBox>
          <Divider />
          <MDBox px={1}>
            <AdminSearch value={query} onChange={setQuery} placeholder="Cari SID/ID/Bahagian..." />
            <DataTable
              table={{ columns, rows: tableRows }}
              isSorted={false}
              entriesPerPage={{ defaultValue: 10, entries: [10, 20, 50] }}
              canSearch={false}
              showTotalEntries
              noPagination={false}
              fontSize={"0.85rem"}
              dense
            />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
